/**
 * Trumpet transposition.
 *
 * Canonical model: every note the app stores, generates, grades and looks up
 * in brassDict / scalesDict is a WRITTEN pitch for the player's physical
 * trumpet ("D4", "F#3", "Cb4"; scientific octaves, so B#3 sounds as C4 and
 * Cb4 as B3, exactly as in brassDict). Nothing here changes that.
 *
 * Three pitches are kept apart:
 *   written    - what the physical trumpet reads. Fingering lookup and
 *                grading ALWAYS use this.
 *   concert    - what actually sounds: written + the key's offset.
 *   displayed  - what is drawn on the staff: written, or concert when the
 *                player picked Concert Pitch.
 *
 * Fingerings for a written note are identical on B♭, C and D trumpets, so the
 * single brassDict.trumpet table serves all three keys.
 *
 * Transposition is interval based (a Bb trumpet sounds a major 2nd below the
 * written note), so spelling stays correct: written F# -> concert E, written
 * Ab -> Gb. Where that would need a double accidental (written Fb on Bb
 * trumpet = Ebb) the note is respelled to its plain enharmonic (D), because
 * the app's dictionaries and note parsers only use single accidentals.
 */

export const TRUMPET_KEYS = {
  // semitones: concert minus written. letterSteps: the matching interval size
  // in staff letters (major 2nd = 1 letter step) so the spelling stays right.
  Bb: { value: 'Bb', label: 'B♭ Trumpet', semitones: -2, letterSteps: -1 },
  C: { value: 'C', label: 'C Trumpet', semitones: 0, letterSteps: 0 },
  D: { value: 'D', label: 'D Trumpet', semitones: 2, letterSteps: 1 },
  // Already offered by the existing "Type" setting (instrumentTypes.js), so it is kept.
  Eb: { value: 'Eb', label: 'E♭ Trumpet', semitones: 3, letterSteps: 2 },
};
export const DEFAULT_TRUMPET_KEY = 'Bb';

/**
 * 'written' - the part written for the selected trumpet (default).
 * 'concert' - the sounding pitch.
 * 'bbPart'  - the ORIGINAL behaviour of the Type setting on the Flashcards
 *             page: the card shows a B♭ trumpet part and a C / D / E♭ trumpet
 *             is fingered to sound the same. Kept so players who already saved
 *             a non-B♭ Type see no change. It is the same as 'written' on a
 *             B♭ trumpet, and Scales never used it.
 */
export const NOTATIONS = [
  { value: 'written', label: 'Written Pitch' },
  { value: 'concert', label: 'Concert Pitch' },
];
export const BB_PART_NOTATION = { value: 'bbPart', label: 'B♭ Part (transposed)' };
export const DEFAULT_NOTATION = 'written';

/** Anything unknown or missing (old saved settings) falls back to the old behaviour. */
export const normalizeTrumpetKey = (key) => (TRUMPET_KEYS[key] ? key : DEFAULT_TRUMPET_KEY);
export const normalizeNotation = (value) =>
  value === 'concert' || value === 'bbPart' ? value : DEFAULT_NOTATION;

/**
 * The notation actually in force for saved options. Options saved before this
 * feature have no `notation`; a non-B♭ Type in them meant the B♭-part reading,
 * so that is what they keep. Scales never had it, so there it reads as written.
 */
export const resolveNotation = (options = {}, { page } = {}) => {
  const type = normalizeTrumpetKey(options.instrumentType);
  const saved = options.notation;
  const notation =
    saved === 'concert' || saved === 'written' || saved === 'bbPart'
      ? saved
      : type === 'Bb'
        ? 'written'
        : 'bbPart';
  if (notation === 'bbPart' && (type === 'Bb' || page === 'scales')) return 'written';
  return notation;
};

/* ---------- note maths ---------- */

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const LETTER_SEMITONES = [0, 2, 4, 5, 7, 9, 11];
const NOTE_PATTERN = /^([A-G])(##|bb|#|b)?(-?\d+)$/;
const ACCIDENTAL_VALUE = { '': 0, '#': 1, b: -1, '##': 2, bb: -2 };
const ACCIDENTAL_NAME = { 0: '', 1: '#', '-1': 'b' };

export const parseNote = (note) => {
  const match = NOTE_PATTERN.exec(note);
  if (!match) return null;
  return {
    letter: match[1],
    accidental: match[2] || '',
    octave: Number(match[3]),
  };
};

/** Absolute semitone number (C0 = 0). Cb4 = B3, B#3 = C4, as in brassDict. */
export const noteToSemitone = (note) => {
  const parsed = parseNote(note);
  if (!parsed) return null;
  return (
    parsed.octave * 12 +
    LETTER_SEMITONES[LETTERS.indexOf(parsed.letter)] +
    ACCIDENTAL_VALUE[parsed.accidental]
  );
};

/**
 * Moves a note by an interval given as a number of staff letters and
 * semitones. Returns null for an unparseable note.
 */
export const shiftNote = (note, letterSteps, semitones) => {
  const parsed = parseNote(note);
  if (!parsed) return null;

  const target = noteToSemitone(note) + semitones;
  let position = parsed.octave * 7 + LETTERS.indexOf(parsed.letter) + letterSteps;

  const accidentalAt = (pos) => {
    const octave = Math.floor(pos / 7);
    return target - (octave * 12 + LETTER_SEMITONES[((pos % 7) + 7) % 7]);
  };

  // A double accidental can't be spelled by this app: use the plain neighbour.
  let accidental = accidentalAt(position);
  while (accidental > 1) {
    position += 1;
    accidental = accidentalAt(position);
  }
  while (accidental < -1) {
    position -= 1;
    accidental = accidentalAt(position);
  }

  const octave = Math.floor(position / 7);
  const letter = LETTERS[((position % 7) + 7) % 7];
  return `${letter}${ACCIDENTAL_NAME[accidental]}${octave}`;
};

/* ---------- written / concert / displayed ---------- */

const isTrumpet = (instrument) => instrument === 'trumpet';

/** Sounding pitch of a written note on the given trumpet key. */
export const writtenToConcert = (written, trumpetKey = DEFAULT_TRUMPET_KEY) => {
  const key = TRUMPET_KEYS[normalizeTrumpetKey(trumpetKey)];
  return shiftNote(written, key.letterSteps, key.semitones);
};

/** The written note that sounds as the given concert pitch on this trumpet key. */
export const concertToWritten = (concert, trumpetKey = DEFAULT_TRUMPET_KEY) => {
  const key = TRUMPET_KEYS[normalizeTrumpetKey(trumpetKey)];
  return shiftNote(concert, -key.letterSteps, -key.semitones);
};

/**
 * What to draw for a written note. Only trumpet in Concert Pitch changes
 * anything; every other instrument and notation returns the note untouched.
 */
export const writtenToDisplayed = (written, { instrument = 'trumpet', trumpetKey, notation } = {}) => {
  if (!isTrumpet(instrument) || normalizeNotation(notation) !== 'concert') return written;
  return writtenToConcert(written, trumpetKey) || written;
};

/** Inverse of writtenToDisplayed: the written note to grade a displayed note against. */
export const displayedToWritten = (displayed, { instrument = 'trumpet', trumpetKey, notation } = {}) => {
  if (!isTrumpet(instrument) || normalizeNotation(notation) !== 'concert') return displayed;
  return concertToWritten(displayed, trumpetKey) || displayed;
};

/** Whole scale / note list, in one call. */
export const writtenListToDisplayed = (notes, settings) =>
  notes.map((note) => writtenToDisplayed(note, settings));

/** Note name without its octave ("Bb3" -> "Bb"). */
export const pitchName = (note) => {
  const parsed = parseNote(note);
  return parsed ? `${parsed.letter}${parsed.accidental}` : note;
};

/**
 * Key name to show for a written scale tonic: "C" written on a Bb trumpet in
 * Concert Pitch is "Bb". The octave only anchors the maths, it isn't returned.
 */
export const displayedTonic = (writtenTonic, settings) =>
  pitchName(scaleToDisplayed([`${writtenTonic}4`], settings)[0]);

/** The sounding key of a written tonic, for the "(Concert X Major)" hint. */
export const concertTonicName = (writtenTonic, trumpetKey) =>
  displayedTonic(writtenTonic, { instrument: 'trumpet', trumpetKey, notation: 'concert' });

/* ---------- audio ---------- */

/**
 * The /sounds/trumpet/<note>.mp3 file to play for a written note.
 *
 * ASSUMPTION (see README/answer): the existing samples are recordings of a
 * B♭ trumpet named by the note that was WRITTEN, so the file for written X
 * sounds a major 2nd below X. A C or D trumpet sounds higher than that, so it
 * has to borrow the B♭ sample whose sounding pitch matches:
 *   C trumpet: file named a major 2nd above the written note,
 *   D trumpet: file named a major 3rd above.
 * For B♭ trumpet (and every other instrument) the note is returned unchanged,
 * so existing audio is untouched.
 */
export const writtenToSampleNote = (written, { instrument = 'trumpet', trumpetKey } = {}) => {
  if (!isTrumpet(instrument)) return written;
  const key = TRUMPET_KEYS[normalizeTrumpetKey(trumpetKey)];
  const bb = TRUMPET_KEYS.Bb;
  const letterSteps = key.letterSteps - bb.letterSteps;
  const semitones = key.semitones - bb.semitones;
  if (letterSteps === 0 && semitones === 0) return written;
  return shiftNote(written, letterSteps, semitones) || written;
};

/* ---------- fingering, audio and scale helpers ---------- */

/**
 * The Type to hand to getCombos() for a note that is on screen as a written
 * (or concert) pitch: written notes are looked up straight in brassDict, i.e.
 * as B♭ trumpet, because every trumpet key fingers a written note the same.
 * Only the old 'bbPart' reading still needs the Type's offset.
 */
export const fingeringType = (instrument, type, notation) =>
  isTrumpet(instrument) && normalizeNotation(notation) !== 'bbPart' ? 'Bb' : type;

/** Sample to play for the note a flashcard holds. 'bbPart' notes are already B♭ sample names. */
export const sampleNoteFor = (note, { instrument = 'trumpet', trumpetKey, notation } = {}) =>
  normalizeNotation(notation) === 'bbPart' ? note : writtenToSampleNote(note, { instrument, trumpetKey });

// The staff draws accidentals from the key signature of the first note, and
// VexFlow only knows these major keys.
const DRAWABLE_MAJOR_TONICS = new Set([
  'C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#', 'F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb',
]);

/**
 * A whole written scale as it should be drawn. Concert pitch on a D trumpet
 * turns F# and C# major into G# and D# major, which have no key signature;
 * those are written as the equal-sounding A♭ and E♭ major instead.
 */
export const scaleToDisplayed = (notes, settings = {}) => {
  const shown = writtenListToDisplayed(notes, settings);
  // Only Concert Pitch on a trumpet changes a scale; every other case is left exactly as stored.
  const transposed = isTrumpet(settings.instrument ?? 'trumpet') && normalizeNotation(settings.notation) === 'concert';
  if (!transposed || shown.length === 0 || DRAWABLE_MAJOR_TONICS.has(pitchName(shown[0]))) return shown;
  return shown.map((note) => shiftNote(note, 1, 0) || note);
};

/* ---------- saved settings ---------- */

/** The settings every screen needs, from the saved 'options' object. */
export const readTranspositionSettings = (options = {}, { page } = {}) => ({
  trumpetKey: normalizeTrumpetKey(options.instrumentType),
  notation: resolveNotation(options, { page }),
});